<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="images" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-1">{{ isAlreadyRegistered ? $t('auth.login.title') : $t('auth.register.title') }}</h4>
              <p v-if="!isAlreadyRegistered" class="mb-3">{{ $t('auth.register.haveAccount') }} <router-link to="/login" class="link-primary">{{ $t('auth.register.login') }}</router-link></p>
              <p v-else class="mb-3">{{ $t('auth.register.alreadyRegisteredMessage', { email: email }) }}</p>
            </div>
            
            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>
            
            <!-- Registration fields - only show when not already registered -->
            <div v-if="!isAlreadyRegistered">
              <!-- Company Name -->
              <div class="mb-3">
                <input
                  type="text"
                  class="form-control"
                  :placeholder="$t('auth.register.companyCode')"
                  v-model="companyCode"
                />
                <div v-if="hasFieldError('companyCode')" class="text-danger text-start">
                  {{ getFieldError('companyCode') }}
                </div>
              </div>
              
              <div class="row">
                <div class="col-sm-6">
                  <div class="mb-3">
                    <input 
                      type="text" 
                      class="form-control" 
                      :placeholder="$t('auth.register.firstName')" 
                      v-model="firstName"
                    />
                    <div v-if="hasFieldError('firstName')" class="text-danger text-start">
                      {{ getFieldError('firstName') }}
                    </div>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="mb-3">
                    <input 
                      type="text" 
                      class="form-control" 
                      :placeholder="$t('auth.register.lastName')"
                      v-model="lastName"
                    />
                    <div v-if="hasFieldError('lastName')" class="text-danger text-start">
                      {{ getFieldError('lastName') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Email field -->
            <div class="mb-3">
              <div class="input-group">
                <input
                  type="email"
                  class="form-control"
                  :placeholder="isAlreadyRegistered ? $t('auth.login.email') : $t('auth.register.email')"
                  v-model="email"
                  :disabled="isEmailFromLink || isDecodingEmail"
                />
                <span v-if="isDecodingEmail" class="input-group-text">
                  <span class="spinner-border spinner-border-sm"></span>
                </span>
              </div>
              <div v-if="hasFieldError('email')" class="text-danger text-start">
                {{ getFieldError('email') }}
              </div>
              <div v-if="isEmailFromLink && !isAlreadyRegistered" class="text-muted small mt-1">
                {{ $t('auth.register.emailPrefilledFromLink') }}
              </div>
              <div v-if="isDecodingEmail" class="text-muted small mt-1">
                {{ $t('auth.register.decodingEmail') }}
              </div>
            </div>
            
            <!-- Phone field - only show for registration -->
            <div v-if="!isAlreadyRegistered" class="mb-3">
              <input
                type="tel"
                class="form-control"
                :placeholder="$t('auth.register.phone')"
                v-model="phone"
              />
              <div v-if="hasFieldError('phone')" class="text-danger text-start">
                {{ getFieldError('phone') }}
              </div>
            </div>
            
            <!-- Password field -->
            <div class="mb-3">
              <div class="position-relative">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control pe-5"
                  :placeholder="isAlreadyRegistered ? $t('auth.login.password') : $t('auth.register.password')"
                  v-model="password"
                />
                <button 
                  type="button" 
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                  @click="showPassword = !showPassword"
                  style="z-index: 10; border: none; background: none;"
                >
                  <i :class="showPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                </button>
              </div>
              <div v-if="hasFieldError('password')" class="text-danger text-start">
                {{ getFieldError('password') }}
              </div>
              <!-- Forgot password link - only show when already registered -->
              <div v-if="isAlreadyRegistered" class="text-end mt-2">
                <a href="#" class="link-primary" @click.prevent="handleForgotPassword">
                  <h6 class="f-w-400 mb-0">{{ $t('auth.login.forgotPassword') }}</h6>
                </a>
              </div>
              <!-- Password strength indicator - only show for registration -->
              <div v-if="!isAlreadyRegistered && password" class="mt-2">
                <div class="progress" style="height: 6px;">
                  <div 
                    class="progress-bar" 
                    :class="passwordStrengthClass"
                    :style="{ width: passwordStrengthPercent + '%' }"
                  ></div>
                </div>
                <small :class="passwordStrengthTextClass">
                  {{ passwordStrengthText }}
                </small>
                <!-- zxcvbn feedback for weak passwords -->
                <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.warning" class="mt-1">
                  <small class="text-warning">
                    <i class="ph-duotone ph-warning me-1"></i>
                    {{ passwordStrength.feedback.warning }}
                  </small>
                </div>
                <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.suggestions.length > 0" class="mt-1">
                  <small class="text-muted">
                    <i class="ph-duotone ph-lightbulb me-1"></i>
                    {{ passwordStrength.feedback.suggestions[0] }}
                  </small>
                </div>
              </div>
            </div>
            
            <!-- Confirm password - only show for registration -->
            <div v-if="!isAlreadyRegistered" class="mb-3">
              <div class="position-relative">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-control pe-5"
                  :placeholder="$t('auth.register.confirmPassword')"
                  v-model="confirmPassword"
                />
                <button 
                  type="button" 
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                  @click="showConfirmPassword = !showConfirmPassword"
                  style="z-index: 10; border: none; background: none;"
                >
                  <i :class="showConfirmPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                </button>
              </div>
              <div v-if="hasFieldError('confirmPassword')" class="text-danger text-start">
                {{ getFieldError('confirmPassword') }}
              </div>
            </div>
            
            <!-- Terms checkbox - only show for registration --> <!-- NOSONAR -->
            <!-- NOSONAR-start -->
            <!-- <div v-if="!isAlreadyRegistered" class="d-flex mt-1 justify-content-between">
              <div class="form-check">
                <input 
                  class="form-check-input input-primary" 
                  type="checkbox" 
                  id="customCheckc1" 
                  :checked="acceptTerms"
                  @click.prevent="handleCheckboxClick"
                  readonly
                />
                <label 
                  class="form-check-label" 
                  :class="hasFieldError('acceptTerms') ? 'text-danger' : 'text-muted'" 
                  for="customCheckc1"
                  style="cursor: pointer;"
                  @click="openTermsModal"
                >
                  {{ $t('auth.register.agreeToTerms') }} 
                  <a href="#" @click.prevent="openTermsModal" class="link-primary">
                    {{ $t('auth.register.termsAndConditions') }}
                  </a>
                </label>
                <div v-if="hasFieldError('acceptTerms')" class="text-danger">
                  {{ getFieldError('acceptTerms') }}
                </div>
                <div v-if="!acceptTerms" class="text-muted small mt-1">
                  {{ $t('auth.register.clickToViewTerms') }}
                </div>
              </div>
            </div> -->
            <!-- NOSONAR-end -->
            
            <div class="d-grid mt-4">
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="handleButtonClick"
                :disabled="isLoading || isDecodingEmail || (!isAlreadyRegistered && !isFormValid)"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ isAlreadyRegistered ? $t('auth.login.loginButton') : $t('auth.register.registerButton') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>

  <!-- Terms and Conditions Modal -->
  <div class="modal fade" :class="{ show: showTermsModal }" :style="{ display: showTermsModal ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('auth.register.termsAndConditions') }}</h5>
          <button type="button" class="btn-close" @click="closeTermsModal"></button>
        </div>
        <div class="modal-body">
          <div class="terms-content">
            <h6>{{ $t('auth.terms.acceptance') }}</h6>
            <p>{{ $t('auth.terms.acceptanceText') }}</p>
            
            <h6>{{ $t('auth.terms.services') }}</h6>
            <p>{{ $t('auth.terms.servicesText') }}</p>
            
            <h6>{{ $t('auth.terms.userAccount') }}</h6>
            <p>{{ $t('auth.terms.userAccountText') }}</p>
            
            <h6>{{ $t('auth.terms.privacy') }}</h6>
            <p>{{ $t('auth.terms.privacyText') }}</p>
            
            <h6>{{ $t('auth.terms.liability') }}</h6>
            <p>{{ $t('auth.terms.liabilityText') }}</p>
            
            <h6>{{ $t('auth.terms.modifications') }}</h6>
            <p>{{ $t('auth.terms.modificationsText') }}</p>
            
            <h6>{{ $t('auth.terms.contact') }}</h6>
            <p>{{ $t('auth.terms.contactText') }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeTermsModal">
            {{ $t('common.actions.close') }}
          </button>
          <button type="button" class="btn btn-primary" @click="acceptTermsFromModal">
            {{ $t('auth.register.acceptAndClose') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal backdrop -->
  <div v-if="showTermsModal" class="modal-backdrop fade show" @click="closeTermsModal"></div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
// Removed vuelidate to prevent infinite loops with dynamic form changes
import Swal from 'sweetalert2';
import authService from '@/services/authService';
import passwordStrengthService from '@/services/passwordStrengthService';
import AuthFooter from '@/components/auth/AuthFooter.vue';

export default {
  name: 'RegisterPage',
  components: {
    AuthFooter
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { t, locale } = useI18n();
    
    const companyCode = ref('');
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const phone = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const acceptTerms = ref(false);
    const error = ref(null);
    const isLoading = ref(false);
    const showTermsModal = ref(false);
    const termsViewed = ref(false);
    const isEmailFromLink = ref(false);
    const isDecodingEmail = ref(false);
    const isAlreadyRegistered = ref(false);
    const suppressValidation = ref(false);
    
    // Password visibility toggles
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    
    // URL parameters for refs
    const urlRefs = ref({
      u: null,
      k: null
    });

    // Advanced password strength calculation using zxcvbn-ts
    const passwordStrength = computed(() => {
      const pwd = password.value;
      if (!pwd) {
        return {
          score: 0,
          strength: 'veryWeak',
          text: '',
          class: 'bg-secondary',
          textClass: 'text-muted',
          percentage: 0,
          feedback: { warning: '', suggestions: [] }
        };
      }
      
      // Prepare user inputs to penalize personal info in passwords
      const userInputs = [];
      if (email.value) {
        const emailParts = email.value.split('@');
        userInputs.push(emailParts[0]); // username part
        userInputs.push(...emailParts[0].split('.')); // split by dots
      }
      if (firstName.value) {
        userInputs.push(firstName.value.toLowerCase());
      }
      if (lastName.value) {
        userInputs.push(lastName.value.toLowerCase());
      }
      if (companyCode.value) {
        userInputs.push(companyCode.value.toLowerCase());
      }
      
      // Add common site-specific terms
      userInputs.push('nozzle', 'supplier', 'portal', 'password');
      
      // Evaluate password using zxcvbn-ts
      const evaluation = passwordStrengthService.evaluatePassword(pwd, userInputs);
      
      return {
        score: evaluation.score,
        strength: evaluation.strength,
        text: passwordStrengthService.getLocalizedStrength(evaluation.strength, t),
        class: passwordStrengthService.getStrengthClass(evaluation.score),
        textClass: passwordStrengthService.getStrengthTextClass(evaluation.score),
        percentage: passwordStrengthService.getStrengthPercentage(evaluation.score),
        feedback: evaluation.feedback,
        guesses: evaluation.guesses,
        crackTime: evaluation.crackTimesDisplay,
        calcTime: evaluation.calculation_time
      };
    });

    const passwordStrengthPercent = computed(() => passwordStrength.value.percentage);
    const passwordStrengthClass = computed(() => passwordStrength.value.class);
    const passwordStrengthText = computed(() => passwordStrength.value.text);
    const passwordStrengthTextClass = computed(() => passwordStrength.value.textClass);

    // Form validation with password strength check
    const isFormValid = computed(() => {
      if (isAlreadyRegistered.value) {
        return email.value && password.value;
      }
      
      return companyCode.value && 
             firstName.value && 
             lastName.value && 
             email.value && 
             password.value && 
             confirmPassword.value && 
             password.value === confirmPassword.value &&
             // acceptTerms.value && // NOSONAR
             passwordStrengthService.meetsMinimumStrength(passwordStrength.value.score, 2);
    });

    // Decode email from URL parameters
    const decodeEmailFromLink = async (unq, key) => {
      try {
        isDecodingEmail.value = true;
        const response = await authService.decodeRegistrationEmail(unq, key);
        
        if (response && response.email) {
          // Temporarily suppress validation to avoid loops
          suppressValidation.value = true;
          
          // Wait for validation to be disabled
          await nextTick();
          
          // Set values safely
          if (response.alreadyRegister) {
            isAlreadyRegistered.value = true;
          }
          
          // Set email and mark as from link
          email.value = response.email;
          isEmailFromLink.value = true;
          error.value = null;
          
          // Wait for all changes to settle
          await nextTick();
          
          // Re-enable validation
          suppressValidation.value = false;
        } else {
          error.value = t('auth.register.emailDecodeError');
          isEmailFromLink.value = false;
        }
      } catch (err) {
        console.error('Email decode error:', err);
        
        // More specific error message based on error type
        if (err.response?.status === 404) {
          error.value = t('auth.register.invalidInvitationLink');
        } else if (err.response?.status >= 500) {
          error.value = t('auth.register.serverError');
        } else {
          error.value = t('auth.register.emailDecodeError');
        }
        
        // Allow manual email entry on any error
        isEmailFromLink.value = false;
        email.value = '';
        suppressValidation.value = false;
      } finally {
        isDecodingEmail.value = false;
      }
    };

    // Extract URL parameters on component mount
    onMounted(async () => {
      // Wait for component to be fully initialized
      await nextTick();
      
      const unq = route.query.unq;
      const key = route.query.key;
      
      if (unq && key) {
        urlRefs.value = {
          u: unq,
          k: key
        };
        // Decode email with small delay to avoid validation conflicts
        setTimeout(async () => {
          await decodeEmailFromLink(unq, key);
        }, 300);
      }
    });

    // Simple validation without vuelidate to avoid loops
    const validationErrors = ref({});
    
    const validateField = (field, value) => {
      if (suppressValidation.value) return true;
      
      const errors = [];
      
      if (isAlreadyRegistered.value) {
        // Login mode validation
        if (field === 'email') {
          if (!value) errors.push(t('auth.register.emailRequired'));
          else if (!/\S+@\S+\.\S+/.test(value)) errors.push(t('auth.register.emailInvalid'));
        }
        if (field === 'password' && !value) errors.push(t('auth.register.passwordRequired'));
      } else {
        // Registration mode validation
        if (field === 'companyCode' && !value) errors.push(t('auth.register.companyCodeRequired'));
        if (field === 'firstName' && !value) errors.push(t('auth.register.nameRequired'));
        if (field === 'lastName' && !value) errors.push(t('auth.register.nameRequired'));
        if (field === 'email') {
          if (!value) errors.push(t('auth.register.emailRequired'));
          else if (!/\S+@\S+\.\S+/.test(value)) errors.push(t('auth.register.emailInvalid'));
        }
        if (field === 'password') {
          if (!value) errors.push(t('auth.register.passwordRequired'));
          else if (value.length < 6) errors.push('Password must be at least 6 characters');
          else if (!passwordStrengthService.meetsMinimumStrength(passwordStrength.value.score, 2)) {
            errors.push('Password is too weak. Please choose a stronger password.');
          }
        }
        if (field === 'confirmPassword') {
          if (!value) errors.push(t('auth.register.confirmRequired'));
          else if (value !== password.value) errors.push(t('auth.register.passwordsNoMatch'));
        }
        // if (field === 'acceptTerms' && !value) errors.push(t('auth.register.termsRequired')); // NOSONAR
      }
      
      validationErrors.value[field] = errors;
      return errors.length === 0;
    };
    
    const validateForm = () => {
      if (suppressValidation.value) return true;
      
      let isValid = true;
      validationErrors.value = {};
      
      if (isAlreadyRegistered.value) {
        isValid = validateField('email', email.value) && isValid;
        isValid = validateField('password', password.value) && isValid;
      } else {
        isValid = validateField('companyCode', companyCode.value) && isValid;
        isValid = validateField('firstName', firstName.value) && isValid;
        isValid = validateField('lastName', lastName.value) && isValid;
        isValid = validateField('email', email.value) && isValid;
        isValid = validateField('password', password.value) && isValid;
        isValid = validateField('confirmPassword', confirmPassword.value) && isValid;
        // isValid = validateField('acceptTerms', acceptTerms.value) && isValid; // NOSONAR
      }
      
      return isValid;
    };
    
    const hasFieldError = (field) => {
      return !suppressValidation.value && validationErrors.value[field] && validationErrors.value[field].length > 0;
    };
    
    const getFieldError = (field) => {
      return validationErrors.value[field] && validationErrors.value[field][0];
    };

    // Live validation: show warning when passwords don't match (without submit)
    watch(confirmPassword, (newValue) => {
      if (suppressValidation.value || isAlreadyRegistered.value) return;
      // Don't nag when empty; only validate when user typed something
      if (!newValue) {
        validationErrors.value['confirmPassword'] = [];
        return;
      }
      validateField('confirmPassword', newValue);
    });

    watch(password, () => {
      if (suppressValidation.value || isAlreadyRegistered.value) return;
      // Re-validate confirm field only if user entered it
      if (confirmPassword.value) {
        validateField('confirmPassword', confirmPassword.value);
      } else {
        validationErrors.value['confirmPassword'] = [];
      }
    });

    const register = async () => {
      error.value = null;
      
      // Validate form
      const isFormValid = validateForm();
      if (!isFormValid) return;
      
      isLoading.value = true;
      
      try {
        const registrationData = {
          tenantName: companyCode.value,
          firstName: firstName.value,
          lastName: lastName.value,
          email: email.value,
          phoneNumber: phone.value,
          password: password.value
        };
        
        // Add refs if URL parameters exist
        if (urlRefs.value.u && urlRefs.value.k) {
          registrationData.refs = {
            u: urlRefs.value.u,
            k: urlRefs.value.k
          };
        }
        
        await authService.register(registrationData);
        
        // Show success notification
        store.dispatch('notify', {
          message: t('common.notification.registerSuccess'),
          type: 'success'
        });
        
        // Reset form
        companyCode.value = '';
        firstName.value = '';
        lastName.value = '';
        email.value = '';
        phone.value = '';
        password.value = '';
        confirmPassword.value = '';
        acceptTerms.value = false;
        validationErrors.value = {};
        
        // Redirect to login page
        router.push('/login');
      } catch (err) {
        error.value = err.response?.data?.error?.message || 
                     err.response?.data?.message || 
                     t('common.notification.registerError');
        console.error('Register error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const handleButtonClick = () => {
      if (isAlreadyRegistered.value) {
        loginWithEncryptedData();
      } else {
        register();
      }
    };

    const loginWithEncryptedData = async () => {
      error.value = null;
      
      // Validate form (only email and password)
      const isFormValid = validateForm();
      if (!isFormValid) return;
      
      isLoading.value = true;
      
      try {
        const loginData = {
          email: email.value,
          password: password.value,
          // Include encrypted refs for backend processing
          refs: {
            u: urlRefs.value.u,
            k: urlRefs.value.k
          }
        };
        
        const result = await authService.login(loginData);
        
        // Store auth data (no additional API call needed)
        await store.dispatch('auth/setAuthData', result);
        
        // Show success notification
        store.dispatch('notify', {
          message: t('common.notification.loginSuccess'),
          type: 'success'
        });
        
        // Redirect to home (which will redirect to appropriate page)
        router.push('/');
      } catch (err) {
        error.value = err.response?.data?.error?.message || 
                     err.response?.data?.message || 
                     t('common.notification.loginError');
        console.error('Login error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    // Accept terms from modal
    const acceptTermsFromModal = () => {
      acceptTerms.value = true;
      showTermsModal.value = false;
    };

    // Mark terms as viewed when modal is opened
    const openTermsModal = () => {
      showTermsModal.value = true;
      termsViewed.value = true;
    };

    const handleCheckboxClick = () => {
      // Always open modal when checkbox is clicked, never change checkbox state
      openTermsModal();
    };

    // Handle modal close without accepting
    const closeTermsModal = () => {
      showTermsModal.value = false;
      // Don't change acceptTerms value - keep it as is
    };

    const handleForgotPassword = async () => {
      if (!email.value) {
        error.value = t('auth.forgotPassword.emailRequired');
        return;
      }

      const result = await Swal.fire({
        title: t('auth.forgotPassword.confirmTitle'),
        text: t('auth.forgotPassword.confirmMessage', { email: email.value }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: t('common.yes'),
        cancelButtonText: t('common.no'),
        confirmButtonColor: '#00A0DF',
        cancelButtonColor: '#6c757d'
      });

      if (result.isConfirmed) {
        isLoading.value = true;
        error.value = null;

        try {
          await authService.requestPasswordReset(email.value);
          
          await Swal.fire({
            title: t('auth.forgotPassword.successTitle'),
            text: t('auth.forgotPassword.success'),
            icon: 'success',
            confirmButtonColor: '#00A0DF'
          });
        } catch (err) {
          const errorMessage = err.response?.data?.error?.message || 
                             err.response?.data?.message || 
                             t('auth.forgotPassword.error');
          
          await Swal.fire({
            title: t('common.notification.error'),
            text: errorMessage,
            icon: 'error',
            confirmButtonColor: '#00A0DF'
          });
          
          console.error('Password reset error:', err);
        } finally {
          isLoading.value = false;
        }
      }
    };

    return {
      companyCode,
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
      acceptTerms,
      error,
      isLoading,
      isEmailFromLink,
      isDecodingEmail,
      isAlreadyRegistered,
      suppressValidation,
      showPassword,
      showConfirmPassword,
      register,
      loginWithEncryptedData,
      handleButtonClick,
      handleForgotPassword,
      hasFieldError,
      getFieldError,
      showTermsModal,
      acceptTermsFromModal,
      termsViewed,
      openTermsModal,
      handleCheckboxClick,
      closeTermsModal,
      isFormValid,
      passwordStrength,
      passwordStrengthPercent,
      passwordStrengthClass,
      passwordStrengthText,
      passwordStrengthTextClass
    };
  }
};
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.auth-form {
  max-width: 460px;
}

.card {
  margin: 1.5rem 0 !important;
}

.card-body {
  padding: 1.75rem 1.5rem;
}

.mb-3 {
  margin-bottom: 0.875rem !important;
}

.text-center img {
  max-width: 90px;
  margin-bottom: 1rem !important;
}

.text-center h4 {
  font-size: 1.35rem;
  margin-bottom: 0.5rem !important;
}

.text-center p {
  margin-bottom: 1.25rem !important;
}

.form-control {
  height: 38px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.btn {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}
</style> 